/**
 * Bundle Free Gift Manager - FoxTheme Integration
 * Monitors cart and automatically removes free gift when bundle items are removed
 * Auto-detects free gift from cart items or uses provided data attribute
 * 
 * @requires Web Components API, FoxTheme
 */
class BundleFreeGiftManager extends HTMLElement {
  constructor() {
    super();
    // Allow manual override via data attribute, but auto-detect if not provided
    this.freeGiftVariantId = this.dataset.freeGiftId || null;
    this.isProcessing = false;
    this.cartUpdateUnsubscriber = null;
  }

  connectedCallback() {
    this.initializeCartMonitoring();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initializeCartMonitoring() {
    // Listen to FoxTheme's pubsub cart update
    if (window.FoxTheme && window.FoxTheme.pubsub) {
      this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (data) => {
          if (data.cart && !this.isProcessing) {
            this.handleCartUpdate(data.cart);
          }
        }
      );
    }
  }

  cleanup() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async handleCartUpdate(cart) {
    if (this.isProcessing || !cart || !cart.items) return;
    
    try {
      this.isProcessing = true;
      
      // Auto-detect free gift variant ID from bundle items if not provided
      if (!this.freeGiftVariantId) {
        this.detectFreeGiftVariantId(cart);
      }
      
      // Check if qualifying bundle items exist (tier 2 or 3)
      // Count actual bundle items (excluding free gifts) to verify tier requirement
      let bundleItemCount = 0;
      let highestTier = 0;
      
      cart.items.forEach(item => {
        if (item.quantity === 0) return;
        
        // Skip free gift items when counting
        const isFreeGift = item.properties?._is_free_gift === 'true' || 
                          item.properties?._is_free_gift === true ||
                          item.properties?._bundle_free_gift != null;
        if (isFreeGift) return;
        
        // Count bundle items
        const isBundle = item.properties?._bundle === 'true' || 
                        item.properties?._bundle === true ||
                        item.properties?._bundle_id != null;
        if (isBundle) {
          bundleItemCount += item.quantity;
          const bundleTier = item.properties?._bundle_tier;
          if (bundleTier) {
            const tierNum = parseInt(bundleTier, 10);
            if (tierNum > highestTier) {
              highestTier = tierNum;
            }
          }
        }
      });
      
      // Only qualify if we have enough items for the tier
      // Tier 2 requires at least 2 items, Tier 3 requires at least 3 items
      const hasQualifyingBundle = (highestTier === 2 && bundleItemCount >= 2) || 
                                  (highestTier === 3 && bundleItemCount >= 3) ||
                                  (highestTier === 4 && bundleItemCount >= 5);

      // Find free gift items
      const freeGiftItems = cart.items.filter(item => {
        if (item.quantity === 0) return false;
        
        // If we have a specific variant ID, check by that
        if (this.freeGiftVariantId) {
          if (String(item.variant_id) === String(this.freeGiftVariantId)) {
            return true;
          }
        }
        
        // Check by bundle_free_gift property (most reliable - comes from bundle picker)
        const bundleFreeGift = item.properties?._bundle_free_gift;
        if (bundleFreeGift) {
          // If we have a specific ID, match it; otherwise accept any bundle free gift
          if (!this.freeGiftVariantId || String(bundleFreeGift) === String(this.freeGiftVariantId)) {
            return true;
          }
        }
        
        // Check by _is_free_gift property (set by bundle-atc-form)
        if (item.properties?._is_free_gift === 'true' || item.properties?._is_free_gift === true) {
          return true;
        }
        
        // Check by _free_gift property (legacy)
        if (item.properties?._free_gift === 'true' || item.properties?._free_gift === true) {
          return true;
        }
        
        return false;
      });

      // RULE 1: If no qualifying bundles but free gifts exist, remove ALL free gifts
      if (!hasQualifyingBundle && freeGiftItems.length > 0) {
        await this.removeFreeGifts(freeGiftItems);
        return;
      }

      // RULE 2: If qualifying bundles exist, ensure ONLY ONE free gift (not one per bundle)
      if (hasQualifyingBundle && freeGiftItems.length > 1) {
        // Keep the first free gift, remove the rest
        const freeGiftsToRemove = freeGiftItems.slice(1);
        await this.removeFreeGifts(freeGiftsToRemove);
        return;
      }

      // RULE 3: If qualifying bundles exist but no free gift, add it back
      if (hasQualifyingBundle && freeGiftItems.length === 0) {
        await this.addFreeGiftIfNeeded(cart, highestTier);
      }
    } catch (error) {
      console.error('[BundleFreeGiftManager] Error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Auto-detect free gift variant ID from cart items
   * Looks for items with _bundle_free_gift property
   * Returns the free gift product ID if found
   */
  detectFreeGiftVariantId(cart) {
    if (!cart.items) return null;
    
    // Find first bundle item with free gift property to get the free gift product ID
    for (const item of cart.items) {
      const bundleFreeGift = item.properties?._bundle_free_gift;
      if (bundleFreeGift) {
        // The property contains the product ID, store it for matching
        this.freeGiftVariantId = bundleFreeGift;
        return bundleFreeGift;
      }
    }
    return null;
  }

  /**
   * Get free gift product ID from bundle items
   */
  getFreeGiftProductId(cart) {
    if (!cart.items) return null;
    
    // Check bundle items for free gift product ID
    for (const item of cart.items) {
      const bundleFreeGift = item.properties?._bundle_free_gift;
      if (bundleFreeGift) {
        return bundleFreeGift;
      }
    }
    
    // Fallback: check bundle picker data attributes
    const bundlePicker = document.querySelector('bundle-picker-v3-2, bundle-picker-v3-1, bundle-picker-v3');
    if (bundlePicker && bundlePicker.dataset.freeGiftId) {
      return bundlePicker.dataset.freeGiftId;
    }
    
    return null;
  }

  /**
   * Get free gift product handle from bundle items or bundle picker
   */
  getFreeGiftHandle(cart) {
    // Check bundle picker data attributes first
    const bundlePicker = document.querySelector('bundle-picker-v3-2, bundle-picker-v3-1, bundle-picker-v3');
    if (bundlePicker && bundlePicker.dataset.freeGiftHandle) {
      return bundlePicker.dataset.freeGiftHandle;
    }
    
    return null;
  }

  /**
   * Add free gift back if bundle qualifies and free gift is missing
   */
  async addFreeGiftIfNeeded(cart, tier) {
    if (!tier || (tier !== 2 && tier !== 3 && tier !== 4)) return;
    
    try {
      const freeGiftProductId = this.getFreeGiftProductId(cart);
      if (!freeGiftProductId) {
        console.log('[BundleFreeGiftManager] No free gift product ID found');
        return;
      }

      // Get free gift variant ID
      const freeGiftHandle = this.getFreeGiftHandle(cart);
      const freeGiftVariantId = await this.getFreeGiftVariantId(freeGiftProductId, freeGiftHandle);
      
      if (!freeGiftVariantId) {
        console.warn('[BundleFreeGiftManager] Free gift variant not found');
        return;
      }

      // Get bundle ID from first bundle item
      let bundleId = null;
      for (const item of cart.items) {
        if (item.properties?._bundle_id) {
          bundleId = item.properties._bundle_id;
          break;
        }
      }
      if (!bundleId) {
        bundleId = `BUNDLE_${Date.now()}`;
      }

      // Get section ID from first bundle item
      let sectionId = null;
      for (const item of cart.items) {
        if (item.properties?._free_with_bundle) {
          sectionId = item.properties._free_with_bundle;
          break;
        }
      }

      // Get sections to bundle for cart update
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { 
          bubbles: true, 
          detail: { sections: sectionsToBundle } 
        })
      );

      // Add free gift to cart
      const items = [{
        id: freeGiftVariantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle: 'true',
          _is_free_gift: 'true',
          _bundle_free_gift: String(freeGiftProductId),
          _free_with_bundle: sectionId || ''
        }
      }];

      // Use FoxTheme's cart add system (same format as bundle-atc-form)
      const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          items,
          sections: sectionsToBundle 
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || `Failed to add free gift: ${response.status}`);
      }

      const result = await response.json();
      
      // Get fresh cart data to ensure we have complete info
      const cartResponse = await fetch('/cart.js');
      const freshCart = await cartResponse.json();
      
      // Publish cart update via FoxTheme pubsub
      FoxTheme.pubsub.publish(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        {
          sources: 'bundle-free-gift-manager',
          cart: { ...result, ...freshCart }
        }
      );
    } catch (error) {
      console.error('[BundleFreeGiftManager] Add free gift error:', error);
    }
  }

  /**
   * Get free gift variant ID from product ID or handle
   */
  async getFreeGiftVariantId(productId, productHandle) {
    try {
      const url = productHandle 
        ? `/products/${encodeURIComponent(productHandle)}.js`
        : `/products/${productId}.js`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error(`Free gift product not found: ${productHandle || productId}`);
        return null;
      }
      
      const product = await response.json();
      
      // Find first available variant
      const variant = product.variants.find(v => v.available);
      
      if (variant) {
        return variant.id;
      } else {
        console.error(`No available variant found for free gift product: ${product.title}`);
        return null;
      }
    } catch (error) {
      console.error('Error loading free gift product:', error);
      return null;
    }
  }

  async removeFreeGifts(freeGiftItems) {
    if (!freeGiftItems || freeGiftItems.length === 0) return;

    try {
      // Build updates object: set quantity to 0 for each free gift
      const updates = {};
      freeGiftItems.forEach(item => {
        if (item.key) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length === 0) {
        console.warn('[BundleFreeGiftManager] No valid item keys found');
        return;
      }

      // Get sections to bundle for cart update
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { 
          bubbles: true, 
          detail: { sections: sectionsToBundle } 
        })
      );

      // Update cart using FoxTheme's cart_update_url
      const body = JSON.stringify({
        updates,
        sections: sectionsToBundle
      });

      const response = await fetch(`${FoxTheme.routes.cart_update_url}`, {
        ...FoxTheme.utils.fetchConfig(),
        body
      });

      if (!response.ok) {
        throw new Error(`Failed to remove free gift: ${response.status}`);
      }

      const parsedState = await response.json();
      
      // Publish cart update via FoxTheme pubsub
      FoxTheme.pubsub.publish(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        {
          sources: 'bundle-free-gift-manager',
          cart: parsedState
        }
      );
    } catch (error) {
      console.error('[BundleFreeGiftManager] Remove free gift error:', error);
    }
  }
}

customElements.define('bundle-free-gift-manager', BundleFreeGiftManager);

